import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import RowActionButton, {
  MenuKeyType,
} from 'commons/components/RowActionButton/RowActionButton.component';
import {
  STODocumentListRowAction,
  STODocumentStatusEnum,
  STOPrintPDFTypeEnum,
} from 'commons/enums';
import { PRIMARY_RED } from 'commons/styles/colors';
import { STODocument } from 'commons/types';
import { stoPrintPDF } from 'commons/utils/printSTODocumentPDF.util';
import { IOpenDialogData } from 'pages/dashboard/order-lists/type/open-dialog-state.interface';
import { RootReducerInterface } from 'redux-stores/reducers';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';

import { RowMenuItemConfig } from '../configs';

const STORowActionBtn: React.FC<{
  stoDocument: STODocument;
  selectedLocation?: number;
}> = ({ stoDocument, selectedLocation }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeLocations } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  const [open, setOpen] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [openDialogData, setOpenDialogData] = useState<IOpenDialogData | null>(
    null,
  );

  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const options: {
    label: string;
    key: STODocumentListRowAction | STOPrintPDFTypeEnum;
  }[] = [
    {
      label: 'Print Surat Pesanan',
      key: STOPrintPDFTypeEnum.SURAT_PESANAN,
    },
  ];

  if (
    [
      STODocumentStatusEnum.SHIPPED,
      STODocumentStatusEnum.ARRIVED,
      STODocumentStatusEnum.RECEIVING,
      STODocumentStatusEnum.COMPLETED,
    ].includes(stoDocument.status)
  ) {
    options.push({
      label: 'Print Invoice',
      key: STOPrintPDFTypeEnum.INVOICE,
    });
  }

  if (stoDocument.status === STODocumentStatusEnum.SHIPPED) {
    options.push({
      label: 'Set Arrived',
      key: STODocumentListRowAction.SET_ARRIVED,
    });
  } else if (stoDocument.status === STODocumentStatusEnum.SHIP_UPDATE_FAILED) {
    options.push({
      label: 'Retry NS Fulfill',
      key: STODocumentListRowAction.RETRY_NS_FULFILL,
    });
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleOpenDialog = (name: string) => {
    setOpenDialogData(RowMenuItemConfig[name.toLocaleLowerCase()]);
    setOpenConfirmation(true);
  };

  const handleCloseDialog = () => {
    setOpenDialogData(null);
    setOpenConfirmation(false);
  };

  const handleMenuClick = (menuKey: MenuKeyType) => {
    const fromWarehouse = activeLocations.find(
      (location) => location.warehouse_id === stoDocument.from_warehouse,
    );
    const toWarehouse = activeLocations.find(
      (location) => location.warehouse_id === stoDocument.to_warehouse,
    );
    switch (menuKey) {
      case STODocumentListRowAction.RETRY_NS_FULFILL:
        handleOpenDialog('retry_ns_fulfill');
        break;
      case STODocumentListRowAction.SET_ARRIVED:
        handleOpenDialog('set_arrived');
        break;
      case STOPrintPDFTypeEnum.SURAT_PESANAN:
      case STOPrintPDFTypeEnum.INVOICE:
        if (fromWarehouse && toWarehouse) {
          stoPrintPDF(stoDocument, fromWarehouse, toWarehouse, menuKey);
        } else {
          dispatch(
            snackbarSetData({
              open: true,
              message: 'Location not found',
              color: PRIMARY_RED,
            }),
          );
        }
        break;
      default:
    }
  };

  return (
    <div style={{ display: options.length > 0 ? 'inline-block' : 'none' }}>
      <RowActionButton
        open={open}
        options={options}
        handleClose={handleClose}
        handleMenuClick={handleMenuClick}
        handleToggle={handleToggle}
      />
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(stoDocument.sto_number) || ''}
        onClose={() => handleCloseDialog()}
        openModal={openConfirmation}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            stoDocument.sto_number,
            selectedLocation,
          )
        }
      />
    </div>
  );
};
export default STORowActionBtn;
